import React, { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';

import EditIcon from '@mui/icons-material/EditOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import Table from '../shared/table';
import CompanyCell from '../shared/table/CompanyCell';

import './surgeons.scss';

const SurgeonsTable = ({
  surgeons = [],
  onEditClick = () => {},
  onDeleteClick = () => {},
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  users = [],
  distributors = [],
  hospitals = [],
  companies = []
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const getUserName = useCallback((uid) => {
    const user = users.find((u) => u.uid === uid);
    return user ? `Dr ${user.firstName} ${user.lastName}` : '';
  }, [users]);

  const getDistributorName = useCallback((id) => {
    const distributor = distributors.find((d) => d.id === id);
    return distributor?.name || '';
  }, [distributors]);

  const getDistributorCountry = useCallback((id) => {
    const distributor = distributors.find((d) => d.id === id);
    return distributor?.country || '';
  }, [distributors]);

  const getHospitalNames = useCallback((ids) => {
    if (!ids) {
      return '';
    }

    const names = ids.map((id) => hospitals?.find((item) => item.id === id)?.name);

    return names?.filter((name) => !!name)?.join(', ');
  }, [hospitals]);

  const columns = useMemo(() => {
    return [
      {
        title: 'Name',
        field: 'userId',
        type: 'custom',
        sort: true,
        formatter: (value) => (
          <span style={{ fontWeight: 'bold' }}>{getUserName(value)}</span>
        )
      },
      {
        title: 'Company',
        field: 'company',
        type: 'custom',
        sort: true,
        formatter: (value) => {
          const company = companies?.find((c) => c.id === value);
          return company ? <CompanyCell company={company} /> : null;
        },
      },
      { title: 'Distributor', field: 'distributor', type: 'custom', sort: true, formatter: getDistributorName },
      {
        title: 'Country',
        field: 'distributor',
        type: 'custom',
        filter: true,
        formatter: (value) => (
          value ? (
            <Chip
              size="small"
              label={getDistributorCountry(value)}
              variant="outlined"
              sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)' }}
            />
          ) : null
        )
      },
      { title: 'Hospitals', field: 'hospitals', type: 'custom', formatter: getHospitalNames },
      {
        title: '',
        field: 'id',
        type: 'custom',
        filter: false,
        width: 50,
        formatter: (value, row) => (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-controls={`${row.id}_actions`}
              aria-haspopup="true"
              aria-label="actions"
              color="inherit"
              id={`${row.id}_actions`}
            >
              <MoreIcon color="secondary" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              elevation={1}
              id={`${row.id}_actions`}
              keepMounted
              onClose={handleMenuClose}
              open={Boolean(anchorEl) && anchorEl.id === `${row.id}_actions`}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  onEditClick(row);
                  handleMenuClose();
                }}
              >
                <EditIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                Edit Surgeon
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  onDeleteClick(row?.id);
                  handleMenuClose();
                }}
              >
                <DeleteIcon fontSize="small" style={{ marginRight: 8, fill: '#F04438' }} />
                <Typography sx={{ color: '#F04438' }}>Delete Surgeon</Typography>
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ];
  }, [surgeons, companies, users, anchorEl]);

  return (
    <Fragment>
      <Table
        stickyTable
        rows={surgeons?.map((surgeon) => ({
          ...surgeon,
          country: surgeon?.country || '',
        }))}
        columns={columns}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Fragment>
  );
};

SurgeonsTable.propTypes = {
  surgeons: PropTypes.array,
  distributors: PropTypes.array,
  onVerifyClick: PropTypes.func,
  onSMSClick: PropTypes.func
};

export default SurgeonsTable;
